import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import axios from '@/services'
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import client_lookups from '@/assets/json/clientLookups.json'
import { useFormStore } from '@/stores/form'
import { useLookupsStore } from '@/stores/lookups'

/**
 * this is a module specific store.
 * related services that might use this store:
 * so yeah similar to an angular controller
 *
 * familyBookAddWifeOrHusband
 * issueFirstFamilyBook
 * guardianshipFileCancellation
 * naturalizationRequest
 * femaleChildrenNaturalizationRequest
 * citizenWifeNaturalizationRequest
 * removePersonDueToDivorce
 * familyBookAddChild
 * maritalStatusModification
 * citizenMothersNameModification
 * citizenBirthdateModification
 * citizenAgeEstimation
 * citizenFirstNameModification
 * citizenAddingAnExistingNickname
 * issueFamilyBookInReplaceOrDamage
 * familyBookSeparateRegistrationAfter30
 * familyBookReplacement
 * guardianship
 * issueNewFamilyBookDueDivorcing
 */

export const usePreloadStore = defineStore('preloadStore', () => {
  const authStore = useAuthStore()
  const lookupsStore = useLookupsStore()
  const route = useRoute()
  const departments = ref([])

  const handleApiPreloads = async (preloads) => {
    departments.value = await lookupsStore.fetchLookup('/api/lookup/departments')

    let promises = []
    preloads.forEach((pl) => {
      promises = pl.api.map(async (a) => {
        if (
          a.path.includes('__reactive__') &&
          a.path.includes('familyMemInfoByIdentityNumberOrUnifiedNumber')
        ) {
          a.path = a.path.replaceAll('__reactive__', authStore.user.identityNumber)
        }

        if (Object.values(a.body || {}).includes('__reactive__')) {
          const newBody = JSON.parse(
            JSON.stringify(a.body).replaceAll('__reactive__', route.query.personUN)
          )
          Object.assign(a.body, newBody)
        }

        const requestConfig = {
          method: a.method,
          url: a.path
        }

        if (a.method == 'POST') {
          requestConfig.data = a.body
        }

        const data = await axios.request(requestConfig)
        return {
          api: a,
          data
        }
      })
    })

    return await Promise.all(promises)
  }

  const prepareForm = async (preloads) => {
    const formStore = useFormStore()
    const [familyMemberInfo, members] = await handleApiPreloads(preloads)

    const { identityNumber, unifiedNumber, englishFullName, arabicFullName } = familyMemberInfo.data
    const { personUN, familyRelationship, wifeCityCode, wifeFamilySequency } = route.query
    const {
      relationships: { parent, samePerson },
      adminRegions: { ain, dhafra, abuDhabi, dubai },
      dubaiEmirateId
    } = client_lookups

    const {
      passportNumber,
      familyBookCityCode,
      familySequence,
      arabicFatherName,
      arabicFirstName1,
      arabicFourthName,
      arabicGrandName,
      englishFatherName,
      englishFirstName,
      englishFourthName,
      englishGrandName,
      faithId,
      professionId,
      religionId
    } = authStore.individualInfo

    Object.assign(formStore.draft.request, {
      applicantIdentityNumber: identityNumber,
      personUnifiedNumber: unifiedNumber,
      applicantEngFullName: englishFullName,
      applicantArbFullName: arabicFullName,
      familyMemEngFullName: englishFullName,
      familyMemArbFullName: arabicFullName,
      identityNumber,
      localPassportNumber: passportNumber,
      familyBookCityCode: familyBookCityCode,
      familyBooKFamilySequence: familySequence,
      applicantArbFatherName: arabicFatherName,
      applicantArbFirstName: arabicFirstName1,
      applicantArbFourthName: arabicFourthName,
      applicantArbThirdName: arabicGrandName,
      applicantEngFatherName: englishFatherName,
      applicantEngFirstName: englishFirstName,
      applicantEngFourthName: englishFourthName,
      applicantEngThirdName: englishGrandName,
      applicantFaithId: faithId,
      applicantProfessionId: professionId,
      applicantReligionId: religionId,
      arabicName: arabicFullName,
      englishName: englishFullName,
      sponsorNumber: unifiedNumber
    })

    // also handled in setRelationship useDropdownAction
    if (+familyRelationship === +parent) {
      formStore.draft.request.lookupLocalRelationId = samePerson
    } else {
      formStore.draft.request.lookupLocalRelationId = +familyRelationship
    }

    if (members.data?.collection?.length > 0) {
      const [
        {
          cityCode,
          familySequence,
          identityNumber,
          unifiedNumber,
          arabicName,
          englishName,
          arabicMotherName,
          englishMotherName,
          arbFatherMotherName,
          dateOfBirth,
          arabicFirstName,
          familyRecImmigDepartmentId
        }
      ] = members.data.collection

      const mainDepartmentId = departments.value.find(
        (d) => d.id == familyRecImmigDepartmentId
      )?.administrativeRegionId

      Object.assign(formStore.draft.request, {
        mainDepartmentId,
        familyBookCityCode: cityCode,
        familyBooKFamilySequence: familySequence,
        familyMemIdnNumber: identityNumber,
        familyMemUnNumber: unifiedNumber,
        familyMemArbFullName: arabicName,
        familyMemEngFullName: englishName,
        currentArbMotherName: arabicMotherName,
        currentEngMotherName: englishMotherName,
        currentArbFatherMotherName: arbFatherMotherName,
        currentDateOfBirth: dateOfBirth,
        currentFirstName: arabicFirstName
      })

      if (mainDepartmentId) {
        if ([ain, dhafra, abuDhabi].includes(mainDepartmentId)) {
          formStore.draft.request.localEmirateId = abuDhabi
        } else if (mainDepartmentId === dubai) {
          formStore.draft.request.localEmirateId = dubaiEmirateId
        } else {
          formStore.draft.request.localEmirateId = mainDepartmentId
        }
      }
    }
  }

  const actions = { prepareForm }

  return { ...actions }
})

export default async function () {
  const requests = [
    {
      api: [
        {
          method: 'GET',
          path: '/api/individual/familyMemInfoByIdentityNumberOrUnifiedNumber/__reactive__',
          field_mappings: {}
        },
        {
          method: 'POST',
          path: '/api/individual/citizen/getFamilyBookMembers',
          body: {
            UnifiedNumber: '__reactive__',
            pageSize: 500,
            currentPage: 1,
            serviceTransactionId: 733,
            FamilyRelationId: 1
          }
        }
      ]
    }
  ]

  await usePreloadStore().prepareForm(requests)
}
